import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { HoneraryBudgetBar } from '@/features/phase2/HoneraryBudgetBar';
import { HonorariumForm } from '@/features/phase2/HonorariumForm';
import { useAuditEvents } from '@/queries/audit';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const HONORARIUM_BUDGET_USD: number = (() => {
  const raw: unknown = import.meta.env.VITE_HONORARIUM_BUDGET_USD;
  if (typeof raw !== 'string' || !raw) return 300;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 300;
})();

export const Route = createFileRoute('/_authed/phase2/member-stories')({
  component: MemberStoriesScreen,
});

function MemberStoriesScreen() {
  const { t } = useTranslation('phase2');
  const audit = useAuditEvents();
  const [storyId, setStoryId] = React.useState('');
  const [working, setWorking] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);

  // Audit-driven approximation: each `admin.member_story.honorarium` event with
  // `state='paid'` would advance the spend counter. The audit endpoint does not
  // expose `before/after` payloads in the list response, so we use the count of
  // paid events as a proxy until backend exposes the amounts. Open question
  // tracked in phases/phase-5-phase2-editorial.md §7.
  const paidCount = React.useMemo(() => {
    const events = audit.data ?? [];
    return events.filter((e) => e.action === 'admin.member_story.honorarium').length;
  }, [audit.data]);
  const approxSpend = paidCount * 50; // unconfirmed estimate per story

  function open(e: React.FormEvent) {
    e.preventDefault();
    if (!UUID.test(storyId.trim())) {
      setError(t('honorarium.invalidId', { defaultValue: 'Story id must be a UUID.' }));
      return;
    }
    setError(undefined);
    setWorking(storyId.trim());
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('memberStories.title', { defaultValue: 'Member stories — honorarium ledger' })}
        </h1>
        <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
          {t('memberStories.subtitle', {
            defaultValue:
              'Track honorarium state per story (Strategy §09 earmark = $300). The before/after snapshot lives in the audit log; toggling "paid contributor" surfaces transparency on the public list.',
          })}
        </p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <HoneraryBudgetBar spend={approxSpend} budget={HONORARIUM_BUDGET_USD} />
          <p className="mt-2 text-xs text-[color:var(--color-fg-muted)]">
            {t('honorarium.budgetCaveat', {
              defaultValue:
                'Approximate — based on count of audit events. Exact dollar tracking needs backend support.',
            })}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={open} className="space-y-3">
            <div>
              <Label htmlFor="story-id">
                {t('honorarium.openStory', { defaultValue: 'Open story by ID' })}
              </Label>
              <Input
                id="story-id"
                dir="ltr"
                className="font-mono"
                value={storyId}
                onChange={(e) => setStoryId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                aria-invalid={Boolean(error)}
              />
              <p className="mt-1 text-xs text-[color:var(--color-fg-muted)]">
                {t('honorarium.idHint', {
                  defaultValue:
                    'No story-list endpoint is documented yet. Paste a story id from the audit log or your editorial workflow.',
                })}
              </p>
              {error ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">
                  {error}
                </p>
              ) : null}
            </div>
            <Button type="submit" disabled={!storyId}>
              {t('honorarium.open', { defaultValue: 'Open ledger' })}
            </Button>
          </form>
        </CardContent>
      </Card>

      {working ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <h2 className="text-base font-semibold">
              {t('honorarium.editorTitle', {
                defaultValue: 'Story {id}',
                id: `${working.slice(0, 8)}…`,
              })}
            </h2>
            <HonorariumForm storyId={working} onSaved={() => undefined} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
